import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CardActionArea
} from '@mui/material';
import {
  Add as AddIcon,
  CreditCard,
  Warning,
  AttachMoney
} from '@mui/icons-material';
import { walletAPI } from '../services/api';

export default function Wallet() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await walletAPI.getStats();
        if (!cancelled) setStats(res && typeof res === 'object' ? res : { activePolicies: 0, totalAnnualPremium: 0, expiringSoon: 0, policies: [] });
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || e.message || 'Failed to load wallet');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const policies = stats?.policies || stats?.data?.policies || [];
  const activeCount = stats?.activePolicies ?? stats?.data?.activePolicies ?? policies.length;
  const totalPremium = stats?.totalAnnualPremium ?? stats?.data?.totalAnnualPremium ?? 0;
  const expiringSoon = stats?.expiringSoon ?? stats?.data?.expiringSoon ?? 0;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—';
  const formatCurrency = (n, c = 'USD') => (n != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: c }).format(n) : '—');

  if (loading) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography>Loading wallet…</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 3 }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">Wallet Overview</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/wallet/add')}>
          Add Policy
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">Active Policies</Typography>
              <Typography variant="h4">{activeCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">Annual Premium</Typography>
              <Typography variant="h6">{formatCurrency(totalPremium)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2">Expiring Soon</Typography>
              <Typography variant="h4">{expiringSoon}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" sx={{ mb: 2 }}>Your Policies</Typography>

      {policies.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>No policies yet</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>Add a policy by lookup, document upload, or manual entry.</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/wallet/add')}>
              Add Policy
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {policies.map((p) => (
            <Grid item xs={12} key={p.id}>
              <Card>
                <CardActionArea onClick={() => navigate(`/wallet/policy/${p.id}`)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        {p.insurer_logo && <Box component="img" src={p.insurer_logo} alt="" sx={{ height: 32, mr: 1, verticalAlign: 'middle' }} />}
                        <Typography variant="subtitle1" fontWeight="bold">{p.policy_name || p.policy_type}</Typography>
                        <Typography variant="body2" color="textSecondary">{p.insurer_name}</Typography>
                        <Typography variant="body2">{(p.policy_number || '').toString()} · Expires: {formatDate(p.end_date)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/wallet/policy/${p.id}`); }}>View Details</Button>
                        <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/cards`); }}>Digital Card</Button>
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}
